import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AssignRouteModal, {
  AssignRouteFormValues,
} from "app/components/Modal/AssignRouteModal";
import { SelectedDeliveryItem } from "app/components/Shared/SelectedDeliveriesList";
import { DriverPanel } from "app/components/DriverPanel/DriverPanel";
import { MapScreen } from "app/components/MapScreen/MapScreen";
import { useAuthContext } from "app/components/Auth/AuthContext";
import {
  AuthData,
  clearAuthData,
  getOdooApiHeaders,
  getStoredAuthData,
  isEmbeddedMode,
  odooApiFetch,
  subscribeToAuthUpdates,
} from "app/utils/auth";
import {
  FILTER_FIELD_SX,
  HEADER_ICON_BUTTON_SX,
  HEADER_PAPER_SX,
  HEADER_TOOLBAR_SX,
} from "app/components/Layout/filterToolbarStyles";
import {
  buildDeliveryPartnersUrl,
  extractOdooResults,
  fetchDriverLocations,
  mergeDriverLocations,
  serializeOdooFilters,
  splitDeliveryPartners,
  DeliveryPartner,
  buildAssignableRouteFilters,
  reassignPickingsToRoute,
} from "app/utils/odooApi";
import { handleApiError, notifyApiError } from "app/utils/apiErrorHandler";
import { buildAssignPickingsPayload } from "app/utils/assignPickings";
import {
  getInitialDeliveryPartnersState,
  saveDeliveryPartnersToStorage,
} from "app/utils/deliveryPartnersCache";
import {
  ROUTE_STATE_LABELS,
  formatOdooTime,
  getRelationName,
} from "app/utils/odooDisplay";
import { getUserTimezone, localDateRangeToUtc } from "app/utils/timezone";
import {
  ROUTE_DATE_RANGE_PRESET_OPTIONS,
  ROUTE_DATE_RANGE_DEFAULT_PRESET,
  DateRangePresetId,
  CUSTOM_DATE_RANGE_PRESET,
  RouteDateRangePresetValue,
  getDateRangeForPreset,
} from "app/utils/dateRangePresets";
import {
  DeliveryRoute,
  UnassignedPicking,
} from "app/types/route";
import { RouteMap } from "app/components/RouteMap/RouteMap";
import { RouteMapErrorBoundary } from "app/components/RouteMap/RouteMapErrorBoundary";
import { UnassignedOrdersPanel } from "app/components/UnassignedOrders/UnassignedOrdersPanel";
import { RouteStopsPanel } from "app/components/RouteStops/RouteStopsPanel";
import { KnowledgeButton } from "app/components/Knowledge/KnowledgeButton";
import { KnowledgePanel } from "app/components/Knowledge/KnowledgePanel";
import { DriverOption, DriverMapFocus, toDriverMapFocus } from "app/types/driver";
import { useDeliveryDashboardSettings } from "app/hooks/useDeliveryDashboardSettings";
import {
  getCachedRouteDetail,
  invalidateRouteDetailCache,
  setCachedRouteDetail,
} from "app/utils/routeDetailCache";
import { invalidateRouteMapDataCache } from "app/utils/routeMapDataCache";

const DATE_FORMAT = "MM/DD/YYYY";

const ROUTE_STATE_COLORS: Record<string, string> = {
  draft: "#94a3b8",
  assigned: "#3b82f6",
  loaded: "#8b5cf6",
  in_progress: "#f59e0b",
  done: "#22c55e",
  cancelled: "#ef4444",
};

type RouteStateFilter = "all" | "active" | "draft" | "assigned" | "loaded" | "in_progress" | "done" | "cancelled";

const panelSx = {
  bgcolor: "#fff",
  border: "1px solid #e5e9ef",
  borderRadius: "14px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column" as const,
  minHeight: 0,
};

function RouteStateChip({ state }: { state?: string }) {
  if (!state) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={ROUTE_STATE_LABELS[state] || state}
      sx={{
        height: 24,
        bgcolor: `${ROUTE_STATE_COLORS[state] || "#94a3b8"}22`,
        color: ROUTE_STATE_COLORS[state] || "#64748b",
        fontWeight: 600,
        fontSize: "0.72rem",
        textTransform: "capitalize",
      }}
    />
  );
}

export function RouteDashboard() {
  const embedded = isEmbeddedMode();
  const navigate = useNavigate();
  const contextAuth = useAuthContext();
  const [authData, setAuthData] = useState<AuthData>(
    () => contextAuth || getStoredAuthData() || {}
  );
  const access_token = authData.access_token ?? "";
  const userTimezone = getUserTimezone(authData);

  const [fromDate, setFromDate] = useState<Dayjs | null>(() => {
    const { start } = getDateRangeForPreset(
      ROUTE_DATE_RANGE_DEFAULT_PRESET,
      getUserTimezone(getStoredAuthData())
    );
    return start;
  });
  const [toDate, setToDate] = useState<Dayjs | null>(() => {
    const { end } = getDateRangeForPreset(
      ROUTE_DATE_RANGE_DEFAULT_PRESET,
      getUserTimezone(getStoredAuthData())
    );
    return end;
  });
  const [dateRangePreset, setDateRangePreset] =
    useState<RouteDateRangePresetValue>(ROUTE_DATE_RANGE_DEFAULT_PRESET);
  const [stateFilter, setStateFilter] = useState<RouteStateFilter>("active");
  const [routes, setRoutes] = useState<DeliveryRoute[]>([]);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [routeDetail, setRouteDetail] = useState<DeliveryRoute | null>(null);
  const [unassignedOrders, setUnassignedOrders] = useState<UnassignedPicking[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>(
    () => getInitialDeliveryPartnersState().drivers
  );
  const [depots, setDepots] = useState<Array<{ id: number; name: string }>>(
    () => getInitialDeliveryPartnersState().depots
  );
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isAssigningRoute, setIsAssigningRoute] = useState(false);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [driverPanelOpen, setDriverPanelOpen] = useState(false);
  const [focusedDriver, setFocusedDriver] = useState<DriverMapFocus | null>(null);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
  const [isRefreshingAssignData, setIsRefreshingAssignData] = useState(false);
  const [unassignedPanelExpanded, setUnassignedPanelExpanded] = useState(false);
  const [mapScreenOpen, setMapScreenOpen] = useState(false);
  const [stopsPanelOpen, setStopsPanelOpen] = useState(false);
  const [knowledgePanelOpen, setKnowledgePanelOpen] = useState(false);
  const [assignableRoutes, setAssignableRoutes] = useState<DeliveryRoute[]>([]);
  const dashboardSettings = useDeliveryDashboardSettings();
  const selectedRouteIdRef = useRef<number | null>(null);
  selectedRouteIdRef.current = selectedRouteId;

  useEffect(() => {
    if (contextAuth?.access_token) {
      setAuthData(contextAuth);
    }
  }, [contextAuth]);

  useEffect(
    () =>
      subscribeToAuthUpdates((nextAuth) => {
        if (nextAuth) {
          setAuthData(nextAuth);
        }
      }),
    []
  );

  const buildRouteFilters = useCallback(() => {
    const filters: unknown[][] = [];
    if (fromDate) {
      filters.push(["date", ">=", fromDate.format("YYYY-MM-DD")]);
    }
    if (toDate) {
      filters.push(["date", "<=", toDate.format("YYYY-MM-DD")]);
    }
    if (stateFilter === "active") {
      filters.push([
        "state",
        "in",
        ["draft", "assigned", "loaded", "in_progress"],
      ]);
    } else if (stateFilter !== "all") {
      filters.push(["state", "=", stateFilter]);
    }
    return filters;
  }, [fromDate, toDate, stateFilter]);

  const handleFromDateChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }
    const nextFrom = value.startOf("day");
    setDateRangePreset(CUSTOM_DATE_RANGE_PRESET);
    setFromDate(nextFrom);
    if (toDate && nextFrom.isAfter(dayjs(toDate), "day")) {
      setToDate(nextFrom);
    }
  };

  const handleToDateChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }
    const nextTo = value.startOf("day");
    setDateRangePreset(CUSTOM_DATE_RANGE_PRESET);
    setToDate(nextTo);
    if (fromDate && nextTo.isBefore(dayjs(fromDate), "day")) {
      setFromDate(nextTo);
    }
  };

  const handleDateRangePresetChange = (preset: DateRangePresetId) => {
    const { start, end } = getDateRangeForPreset(preset, userTimezone);
    setDateRangePreset(preset);
    setFromDate(start);
    setToDate(end);
  };

  const fetchRoutes = useCallback(async () => {
    if (!access_token) {
      return;
    }
    setIsLoadingRoutes(true);
    try {
      const filters = buildRouteFilters();
      const filterQuery =
        filters.length > 0
          ? `filters=${encodeURIComponent(serializeOdooFilters(filters))}&`
          : "";
      const parsed = await odooApiFetch<{ results?: DeliveryRoute[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.route?${filterQuery}order=${encodeURIComponent("name desc")}&limit=100`,
        access_token
      );
      const list = extractOdooResults<DeliveryRoute>(parsed);
      setRoutes(list);
      setSelectedRouteId((currentId) => {
        if (list.length === 0) {
          setRouteDetail(null);
          return null;
        }
        if (!currentId || !list.some((route) => route.id === currentId)) {
          return list[0].id;
        }
        return currentId;
      });
    } catch (error) {
      console.error(error);
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Could not load routes.",
      });
    } finally {
      setIsLoadingRoutes(false);
    }
  }, [access_token, buildRouteFilters, embedded]);

  const fetchUnassignedOrders = useCallback(async () => {
    if (!access_token) {
      return;
    }
    setIsLoadingOrders(true);
    try {
      const filters: unknown[][] = [
        ["picking_type_code", "=", "outgoing"],
        ["state", "=", "done"],
        ["route_id", "=", false],
        ["company_id", "=", 1],
      ];
      if (fromDate) {
        const startRange = localDateRangeToUtc(fromDate, userTimezone);
        filters.push(["scheduled_date", ">=", startRange.startUtc]);
      }
      if (toDate) {
        const endRange = localDateRangeToUtc(toDate, userTimezone);
        filters.push(["scheduled_date", "<=", endRange.endUtc]);
      }
      const parsed = await odooApiFetch<{ results?: UnassignedPicking[] }>(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/stock.picking/?filters=${encodeURIComponent(
          serializeOdooFilters(filters)
        )}&order=${encodeURIComponent("scheduled_date desc")}&limit=200`,
        access_token
      );
      setUnassignedOrders(extractOdooResults(parsed));
    } catch (error) {
      console.error(error);
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Could not load unassigned orders.",
      });
    } finally {
      setIsLoadingOrders(false);
    }
  }, [access_token, embedded, fromDate, toDate, userTimezone]);

  const fetchRouteDetail = useCallback(
    async (routeId: number, options?: { background?: boolean }) => {
      if (!access_token) {
        return;
      }

      const cached = getCachedRouteDetail(routeId);
      if (cached && selectedRouteIdRef.current === routeId) {
        setRouteDetail(cached);
      }

      if (!options?.background) {
        setIsLoadingDetail(true);
      }

      try {
        const detail = await odooApiFetch<DeliveryRoute>(
          `${process.env.REACT_APP_API_BASE_URL}/delivery.route/${routeId}`,
          access_token
        );
        setCachedRouteDetail(routeId, detail);
        if (selectedRouteIdRef.current === routeId) {
          setRouteDetail(detail);
        }
        return detail;
      } catch (error) {
        console.error(error);
        if (selectedRouteIdRef.current === routeId) {
          setRouteDetail(null);
        }
        const result = await handleApiError(error, {
          embedded,
          fallbackMessage: "Could not load route details.",
          onNotFound: () => {
            invalidateRouteDetailCache(routeId);
            setSelectedRouteId(null);
            void fetchRoutes();
          },
        });
        if (result.shouldRetry) {
          await fetchRouteDetail(routeId, options);
        }
      } finally {
        if (!options?.background) {
          setIsLoadingDetail(false);
        }
      }
    },
    [access_token, embedded, fetchRoutes]
  );

  const fetchAssignableRoutes = useCallback(async () => {
    if (!access_token) {
      return;
    }
    try {
      const filters = buildAssignableRouteFilters({
        fromDate: fromDate?.format("YYYY-MM-DD"),
        toDate: toDate?.format("YYYY-MM-DD"),
      });
      const parsed = await odooApiFetch<{ results?: DeliveryRoute[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.route?filters=${encodeURIComponent(
          serializeOdooFilters(filters)
        )}&order=${encodeURIComponent("name desc")}&limit=100`,
        access_token
      );
      setAssignableRoutes(extractOdooResults(parsed));
    } catch (error) {
      console.error(error);
      notifyApiError(error, "Could not load assignable routes.");
    }
  }, [access_token, fromDate, toDate]);

  const fetchDriversAndDepots = useCallback(async (options?: { showPanelLoading?: boolean; includeLocations?: boolean }) => {
    if (!access_token) {
      return;
    }
    if (options?.showPanelLoading) {
      setIsLoadingDrivers(true);
    }
    try {
      const partnersPromise = odooApiFetch<{ results?: DeliveryPartner[] }>(
        buildDeliveryPartnersUrl(),
        access_token
      );
      const locationsPromise =
        options?.showPanelLoading || options?.includeLocations
        ? fetchDriverLocations(access_token).catch(() => [])
        : Promise.resolve([]);

      const [parsed, locations] = await Promise.all([
        partnersPromise,
        locationsPromise,
      ]);
      const { drivers: baseDrivers, depots: nextDepots } = splitDeliveryPartners(
        extractOdooResults(parsed)
      );
      const nextDrivers =
        locations.length > 0
          ? mergeDriverLocations(baseDrivers, locations)
          : baseDrivers;
      setDrivers(nextDrivers);
      setDepots(nextDepots);
      saveDeliveryPartnersToStorage(nextDrivers, nextDepots);
    } catch (error) {
      console.error(error);
      notifyApiError(error, "Could not load drivers and depots.");
    } finally {
      if (options?.showPanelLoading) {
        setIsLoadingDrivers(false);
      }
    }
  }, [access_token]);

  useEffect(() => {
    setFocusedDriver(null);
  }, [selectedRouteId]);

  useEffect(() => {
    if (!driverPanelOpen || !access_token) {
      return;
    }
    void fetchDriversAndDepots({ showPanelLoading: true });
  }, [access_token, driverPanelOpen, fetchDriversAndDepots]);

  useEffect(() => {
    if (!mapScreenOpen || !access_token) {
      return;
    }
    void fetchDriversAndDepots({ includeLocations: true });
  }, [access_token, mapScreenOpen, fetchDriversAndDepots]);

  useEffect(() => {
    if (!access_token) {
      return;
    }
    void fetchRoutes();
  }, [access_token, fetchRoutes]);

  useEffect(() => {
    if (!access_token || !selectedRouteId || !stopsPanelOpen) {
      return;
    }

    const cached = getCachedRouteDetail(selectedRouteId);
    if (cached) {
      setRouteDetail(cached);
      void fetchRouteDetail(selectedRouteId, { background: true });
      return;
    }

    void fetchRouteDetail(selectedRouteId);
  }, [access_token, selectedRouteId, stopsPanelOpen, fetchRouteDetail]);

  useEffect(() => {
    if (!access_token) {
      return;
    }
    void fetchUnassignedOrders();
  }, [access_token, fetchUnassignedOrders]);

  const handleRefreshAll = () => {
    setUnassignedPanelExpanded(false);
    invalidateRouteDetailCache();
    invalidateRouteMapDataCache();
    void fetchRoutes();
    void fetchUnassignedOrders();
    void fetchDriversAndDepots();
    if (selectedRouteId && stopsPanelOpen) {
      void fetchRouteDetail(selectedRouteId);
    }
  };

  const isHeaderBusy =
    isLoadingRoutes ||
    isLoadingOrders ||
    (stopsPanelOpen && isLoadingDetail);

  const handleRefreshAssignRouteData = async () => {
    if (!access_token || isRefreshingAssignData) {
      return;
    }

    setIsRefreshingAssignData(true);
    try {
      await Promise.all([
        fetchRoutes(),
        fetchAssignableRoutes(),
        fetchDriversAndDepots(),
      ]);
      toast.success("Routes and partners updated.");
    } catch {
      toast.error("Could not refresh routes and partners.");
    } finally {
      setIsRefreshingAssignData(false);
    }
  };

  const selectedRouteSummary = useMemo(
    () => routes.find((route) => route.id === selectedRouteId) ?? null,
    [routes, selectedRouteId]
  );

  const displayRoute = useMemo(() => {
    if (routeDetail?.id === selectedRouteId) {
      return routeDetail;
    }
    return selectedRouteSummary;
  }, [routeDetail, selectedRouteId, selectedRouteSummary]);

  const stopsRouteDetail = useMemo(() => {
    if (!selectedRouteId) {
      return null;
    }
    if (routeDetail?.id === selectedRouteId) {
      return routeDetail;
    }
    return getCachedRouteDetail(selectedRouteId) ?? null;
  }, [routeDetail, selectedRouteId]);

  const sortedStops = useMemo(() => {
    const stops = Array.isArray(stopsRouteDetail?.stop_line_ids)
      ? stopsRouteDetail?.stop_line_ids ?? []
      : [];
    return [...stops].sort(
      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
    );
  }, [stopsRouteDetail]);

  const routeDrivers = useMemo(() => {
    return (displayRoute?.driver_line_ids || [])
      .map((line) => getRelationName(line.partner_id, ""))
      .filter(Boolean)
      .join(", ");
  }, [displayRoute]);

  const selectedOrdersForAssign = useMemo<SelectedDeliveryItem[]>(
    () =>
      unassignedOrders
        .filter((order) => selectedOrderIds.includes(order.id))
        .map((order) => ({
          id: order.id,
          pickingNumber: order.name,
          name: order.name,
          partnerName: getRelationName(order.partner_id),
          scheduledDate: order.scheduled_date,
        })),
    [selectedOrderIds, unassignedOrders]
  );

  const handleOpenAssignModal = () => {
    void fetchAssignableRoutes();
    setOpenAssignModal(true);
  };

  const handleRouteSelect = (routeId: number) => {
    setSelectedRouteId(routeId);
  };

  const handleToggleStopsPanel = (
    routeId: number,
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.stopPropagation();
    if (stopsPanelOpen && selectedRouteId === routeId) {
      setStopsPanelOpen(false);
      return;
    }
    setSelectedRouteId(routeId);
    setStopsPanelOpen(true);
  };

  const handleStopReleased = async () => {
    if (selectedRouteId) {
      invalidateRouteDetailCache(selectedRouteId);
      invalidateRouteMapDataCache(selectedRouteId);
    }
    await Promise.all([
      selectedRouteId && stopsPanelOpen
        ? fetchRouteDetail(selectedRouteId)
        : Promise.resolve(),
      fetchUnassignedOrders(),
      fetchRoutes(),
    ]);
  };

  const handleAssignRoute = async (formValues: AssignRouteFormValues) => {
    if (isAssigningRoute || selectedOrderIds.length === 0) {
      return;
    }
    if (formValues.driverId === "") {
      toast.error("Please select a driver.");
      return;
    }
    if (formValues.mode === "existing" && formValues.routeId === "") {
      toast.error("Please select a route.");
      return;
    }
    setIsAssigningRoute(true);
    try {
      const data = await reassignPickingsToRoute(
        buildAssignPickingsPayload(formValues, selectedOrderIds),
        access_token
      );
      toast.success(data?.message || "Deliveries reassigned successfully!");
      setSelectedOrderIds([]);
      setOpenAssignModal(false);
      if (data?.route_id) {
        setSelectedRouteId(data.route_id);
      }
      await fetchRoutes();
      await fetchUnassignedOrders();
      if (data?.route_id) {
        invalidateRouteDetailCache(data.route_id);
        invalidateRouteMapDataCache(data.route_id);
        if (stopsPanelOpen) {
          await fetchRouteDetail(data.route_id);
        }
      }
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Failed to assign route.",
      });
    } finally {
      setIsAssigningRoute(false);
    }
  };

  const toggleOrderSelection = (orderId: number) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleShowDriverLocation = (driver: DriverOption) => {
    if (!selectedRouteId) {
      toast.info("Select a route to view the map.");
      return;
    }

    const focus = toDriverMapFocus(driver);
    if (!focus) {
      toast.info("No location available for this driver.");
      return;
    }

    setFocusedDriver(focus);
  };

  if (!access_token) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: "#7ec597" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f5f6fa",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, sm: 2 },
          pt: embedded ? 1 : { xs: 1.5, sm: 2 },
          pb: 1.25,
        }}
      >
        <Paper elevation={0} sx={HEADER_PAPER_SX}>
          <Box sx={HEADER_TOOLBAR_SX}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: 152 },
                  flexShrink: 0,
                  ...FILTER_FIELD_SX,
                }}
              >
                <InputLabel id="route-date-range-preset-label">Date range</InputLabel>
                <Select
                  labelId="route-date-range-preset-label"
                  id="route-date-range-preset"
                  label="Date range"
                  value={dateRangePreset}
                  renderValue={(selected) => {
                    if (selected === CUSTOM_DATE_RANGE_PRESET) {
                      return "Custom";
                    }
                    return (
                      ROUTE_DATE_RANGE_PRESET_OPTIONS.find(
                        (option) => option.id === selected
                      )?.label ?? "Custom"
                    );
                  }}
                  onChange={(event) => {
                    handleDateRangePresetChange(
                      event.target.value as DateRangePresetId
                    );
                  }}
                >
                  {ROUTE_DATE_RANGE_PRESET_OPTIONS.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_DATE_RANGE_PRESET} sx={{ display: "none" }}>
                    Custom
                  </MenuItem>
                </Select>
              </FormControl>

              <DatePicker
                label="From"
                format={DATE_FORMAT}
                value={fromDate}
                onChange={handleFromDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: { xs: "100%", sm: 150 },
                      flexShrink: 0,
                      ...FILTER_FIELD_SX,
                    },
                  },
                }}
              />
              <DatePicker
                label="To"
                format={DATE_FORMAT}
                value={toDate}
                minDate={fromDate ?? undefined}
                onChange={handleToDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: { xs: "100%", sm: 150 },
                      flexShrink: 0,
                      ...FILTER_FIELD_SX,
                    },
                  },
                }}
              />
            </LocalizationProvider>

            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: "1px",
                height: 32,
                bgcolor: "#e5e9ef",
                flexShrink: 0,
              }}
            />

            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: 168 },
                flexShrink: 0,
                ...FILTER_FIELD_SX,
              }}
            >
              <InputLabel id="route-status-filter-label">Status</InputLabel>
              <Select
                labelId="route-status-filter-label"
                label="Status"
                value={stateFilter}
                onChange={(e) =>
                  setStateFilter(e.target.value as RouteStateFilter)
                }
              >
                <MenuItem value="active">Active routes</MenuItem>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="loaded">Loaded</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="done">Done</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flex: 1, minWidth: 8, display: { xs: "none", lg: "block" } }} />

            {isHeaderBusy && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#5fa67a",
                  flexShrink: 0,
                  px: 0.5,
                }}
              >
                <CircularProgress size={18} sx={{ color: "#7ec597" }} />
                <Typography
                  variant="caption"
                  sx={{ display: { xs: "none", sm: "block" }, whiteSpace: "nowrap" }}
                >
                  Updating...
                </Typography>
              </Box>
            )}

            <IconButton
              aria-label="Toggle map screen"
              size="small"
              onClick={() => setMapScreenOpen((open) => !open)}
              sx={{
                ...HEADER_ICON_BUTTON_SX,
                ...(mapScreenOpen
                  ? { borderColor: "#94a3b8", bgcolor: "#f1f5f9" }
                  : {}),
              }}
            >
              <MapOutlinedIcon fontSize="small" />
            </IconButton>

            <IconButton
              aria-label="Open drivers panel"
              size="small"
              onClick={() => setDriverPanelOpen(true)}
              sx={{
                ...HEADER_ICON_BUTTON_SX,
                ...(driverPanelOpen
                  ? { borderColor: "#5fa67a", bgcolor: "#f0fdf4" }
                  : {}),
              }}
            >
              <PersonOutlineIcon fontSize="small" />
            </IconButton>

            <KnowledgeButton
              active={knowledgePanelOpen}
              onClick={() => setKnowledgePanelOpen(true)}
            />

            <IconButton
              aria-label="Refresh routes"
              size="small"
              disabled={isHeaderBusy}
              sx={HEADER_ICON_BUTTON_SX}
              onClick={handleRefreshAll}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>

            {!embedded && (
              <IconButton
                aria-label="Sign out"
                size="small"
                sx={HEADER_ICON_BUTTON_SX}
                onClick={() => {
                  navigate("/login");
                  clearAuthData();
                }}
              >
                <ExitToAppIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Paper>
      </Box>

      {mapScreenOpen ? (
        <MapScreen
          accessToken={access_token}
          routes={routes}
          unassignedOrders={unassignedOrders}
          drivers={drivers}
          userTimezone={userTimezone}
          onClose={() => setMapScreenOpen(false)}
          onAssignSelected={(pickingIds) => {
            setSelectedOrderIds(pickingIds);
            setMapScreenOpen(false);
            handleOpenAssignModal();
          }}
        />
      ) : (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(200px, 240px) minmax(0, 1fr)",
            xl: "minmax(220px, 280px) minmax(0, 1fr)",
          },
          gridTemplateRows: {
            xs: "minmax(180px, 220px) minmax(320px, 1fr) minmax(240px, 280px)",
            md: "minmax(0, 1fr) minmax(240px, 300px)",
          },
          gap: { xs: 1, sm: 1.25, md: 1.5 },
          px: { xs: 1, sm: 1.5, md: 2 },
          pb: embedded ? 1 : 1.5,
          overflow: unassignedPanelExpanded ? "visible" : "hidden",
        }}
      >
        {/* Routes list */}
        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            gridColumn: { md: 1 },
            gridRow: { xs: 1, md: 1 },
            maxHeight: { xs: 220, md: "none" },
          }}
        >
          <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef1f4" }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Routes ({routes.length})
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {isLoadingRoutes ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <CircularProgress size={28} sx={{ color: "#7ec597" }} />
              </Box>
            ) : routes.length === 0 ? (
              <Typography sx={{ p: 2, color: "#94a3b8", textAlign: "center" }}>
                No routes for this date
              </Typography>
            ) : (
              routes.map((route) => {
                const isSelected = selectedRouteId === route.id;
                const stopsOpenForRoute = stopsPanelOpen && isSelected;

                return (
                  <Box
                    key={route.id}
                    onClick={() => handleRouteSelect(route.id)}
                    sx={{
                      px: 1.5,
                      py: 1.1,
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9",
                      bgcolor: isSelected ? "#f0fdf4" : "transparent",
                      borderLeft: isSelected
                        ? "3px solid #7ec597"
                        : "3px solid transparent",
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={0.75}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={1}
                        >
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {route.name}
                          </Typography>
                          <RouteStateChip state={route.state} />
                        </Box>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {route.picking_count ?? 0} orders ·{" "}
                          {route.total_load_count ?? 0} loads
                        </Typography>
                        {route.depot_id && typeof route.depot_id === "object" && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8", display: "block" }}
                            noWrap
                          >
                            {route.depot_id.name}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title={stopsOpenForRoute ? "Hide stops" : "View stops"}>
                        <IconButton
                          size="small"
                          aria-label={
                            stopsOpenForRoute ? "Hide stops list" : "View stops list"
                          }
                          aria-pressed={stopsOpenForRoute}
                          onClick={(event) => handleToggleStopsPanel(route.id, event)}
                          sx={{
                            flexShrink: 0,
                            width: 28,
                            height: 28,
                            border: "1px solid",
                            borderColor: stopsOpenForRoute ? "#94a3b8" : "#e2e8f0",
                            borderRadius: "7px",
                            bgcolor: stopsOpenForRoute ? "#e2e8f0" : "#fff",
                            color: stopsOpenForRoute ? "#334155" : "#64748b",
                            transform: stopsOpenForRoute ? "none" : "none",
                            transition: "background-color 0.15s ease, border-color 0.15s ease",
                            "&:hover": {
                              bgcolor: "#e2e8f0",
                              borderColor: "#94a3b8",
                              color: "#334155",
                            },
                          }}
                        >
                          <ChevronRightIcon
                            sx={{
                              fontSize: 17,
                              transform: stopsOpenForRoute ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Paper>

        {/* Map + optional collapsible stops panel */}
        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            gridColumn: { md: 2 },
            gridRow: { xs: 2, md: 1 },
            minHeight: { xs: 320, md: 0 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {selectedRouteId && displayRoute && (
              <Box
                sx={{
                  px: 2,
                  py: 0.85,
                  borderBottom: "1px solid #eef1f4",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  bgcolor: "#fafbfc",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {displayRoute.name}
                </Typography>
                <RouteStateChip state={displayRoute.state} />
                {displayRoute.is_round_trip && (
                  <Chip
                    size="small"
                    label="Round trip"
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                )}
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  {getRelationName(displayRoute.depot_id)} ·{" "}
                  {formatOdooTime(displayRoute.start_time)} –{" "}
                  {formatOdooTime(displayRoute.finish_time)} · {routeDrivers || "—"}
                </Typography>
              </Box>
            )}

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
              {stopsPanelOpen && selectedRouteId && (
                <RouteStopsPanel
                  routeDetail={stopsRouteDetail}
                  isLoading={isLoadingDetail && !stopsRouteDetail}
                  sortedStops={sortedStops}
                  accessToken={access_token}
                  embedded={embedded}
                  dashboardSettings={dashboardSettings}
                  onReleased={handleStopReleased}
                  onClose={() => setStopsPanelOpen(false)}
                />
              )}

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  p: { xs: 1, md: 1.25 },
                  bgcolor: "#f8fafc",
                }}
              >
                {!selectedRouteId ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#94a3b8",
                    }}
                  >
                    Select a route to view on the map
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
                    <RouteMapErrorBoundary>
                      <RouteMap
                        routeId={selectedRouteId}
                        accessToken={access_token}
                        active={Boolean(selectedRouteId)}
                        minHeight={0}
                        driverFocus={focusedDriver}
                      />
                    </RouteMapErrorBoundary>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            gridColumn: { xs: 1, md: "1 / 3" },
            gridRow: { xs: 3, md: 2 },
            position: "relative",
            height: { xs: 280, md: 260, xl: 280 },
            minHeight: { xs: 240, md: 240, xl: 260 },
            zIndex: unassignedPanelExpanded ? 30 : 1,
            overflow: "visible",
          }}
        >
          <UnassignedOrdersPanel
            orders={unassignedOrders}
            isLoading={isLoadingOrders}
            selectedOrderIds={selectedOrderIds}
            onToggleOrderSelection={toggleOrderSelection}
            onAssignSelected={handleOpenAssignModal}
            userTimezone={userTimezone}
            embedded={embedded}
            expanded={unassignedPanelExpanded}
            onExpandedChange={setUnassignedPanelExpanded}
          />
        </Box>
      </Box>
      )}

      <DriverPanel
        open={driverPanelOpen}
        onClose={() => setDriverPanelOpen(false)}
        drivers={drivers}
        isLoading={isLoadingDrivers}
        selectedDriverId={focusedDriver?.id ?? null}
        onShowDriverLocation={handleShowDriverLocation}
      />

      <KnowledgePanel
        open={knowledgePanelOpen}
        onClose={() => setKnowledgePanelOpen(false)}
      />

      {openAssignModal && (
        <AssignRouteModal
          openModal={openAssignModal}
          isSubmitting={isAssigningRoute}
          isRefreshing={isRefreshingAssignData}
          routes={assignableRoutes}
          drivers={drivers}
          depots={depots}
          selectedOrders={selectedOrdersForAssign}
          userTimezone={userTimezone}
          modalTitle="Assign Route"
          submitLabel="Assign Route"
          submittingLabel="Assigning route..."
          onRefresh={handleRefreshAssignRouteData}
          handleClose={() => !isAssigningRoute && setOpenAssignModal(false)}
          handleAssignRoute={handleAssignRoute}
        />
      )}
    </Box>
  );
}
